import { cn } from "@/lib/utils";
import { useEffect, useRef } from "react";
import { useMutedAutoplayRetry } from "./useMutedAutoplayRetry";
import { useTapHandlers } from "./useTapHandlers";

interface VideoViewerProps {
  url: string;
  className?: string;
  /** Fires with the element's MediaError (null when unavailable). */
  onError?: (mediaError: MediaError | null) => void;
  /** Hands the mounted <video> element up for the custom viewer controls. */
  onVideoElement?: (el: HTMLVideoElement | null) => void;
  /** A genuine tap on the video surface (drag-tails filtered out). */
  onTap?: () => void;
  /**
   * Fires when the element knows its real dimensions (loadedmetadata) or can
   * render frames (canplay) — whichever comes first. The dialog keeps the
   * video invisible until then: before this, the element's intrinsic size is
   * the POSTER's (the ~560px thumbnail, or a ~32px ThumbHash), so revealing it
   * early painted a thumbnail-sized video that then snapped to full size.
   */
  onReady?: () => void;
  /**
   * Cached thumbnail shown until the first frame is decodable. Handed to the
   * element's own `poster` so the placeholder → first-frame swap happens INSIDE
   * one element — no unmount/mount of a separate preview tree, and no blank gap
   * while the video decodes.
   */
  poster?: string;
}

/**
 * Small-video player for the fullscreen viewer.
 *
 * Renders a BARE <video> — no native `controls`: the element sits inside the
 * zoom transform, and native controls under a CSS transform scale with the
 * video and get DUPLICATED by iOS's system (glass) control overlay. Playback
 * is driven by the viewer's custom controls (play/pause/mute in the bottom
 * bar + the scrub strip), which render outside the transform and never scale.
 * Playback starts via `useMutedAutoplayRetry` (muted fallback for iOS).
 */
export default function VideoViewer({
  url,
  className,
  onError,
  onVideoElement,
  onTap,
  poster,
  onReady,
}: VideoViewerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const tapHandlers = useTapHandlers(onTap);

  useMutedAutoplayRetry(videoRef, url);

  useEffect(() => {
    onVideoElement?.(videoRef.current);
    return () => onVideoElement?.(null);
  }, [onVideoElement]);

  // Deterministic stop on unmount (swipe to an uncached item / image, dialog
  // close). A media element REMOVED from the DOM is paused by the UA only as
  // an async afterthought — and WebKit keeps its download session alive — so
  // stop it explicitly: pause, detach the source, and load() to settle the
  // element into the empty state (same teardown as StreamedVideoViewer and
  // AudioPlayer).
  //
  // The effect BODY re-attaches a stripped src: React 18 StrictMode (dev) runs
  // setup→cleanup→setup on the SAME DOM node, and after the cleanup strips the
  // attribute React never re-applies it (the vdom still records the unchanged
  // src prop) — the element parked at NETWORK_EMPTY: no error event, no error
  // card, autoplay retry hung forever. In production the src is present and
  // the body is a no-op.
  const urlRef = useRef(url);
  urlRef.current = url;
  useEffect(() => {
    const el = videoRef.current;
    if (el && !el.getAttribute("src") && urlRef.current) {
      try {
        el.setAttribute("src", urlRef.current);
      } catch {
        // jsdom lacks full media stubs; re-attach must never throw
      }
    }
    return () => {
      if (!el) {
        return;
      }
      try {
        el.pause();
        el.removeAttribute("src");
        el.load();
      } catch {
        // jsdom lacks full media stubs; teardown must never throw
      }
    };
  }, []);

  // Reveal WITHOUT waiting for an event when the element already has a
  // decodable frame at mount/url-swap (events that fired before this render
  // are gone; waiting for the next one left the poster up until the 2s
  // escape hatch).
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  useEffect(() => {
    const el = videoRef.current;
    if (el && el.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      onReadyRef.current?.();
    }
  }, [url]);

  return (
    <video
      ref={videoRef}
      className={cn(
        // object-contain (not cover): show the WHOLE frame, letterboxed —
        // object-cover cropped portrait/odd-ratio videos in fullscreen.
        // FILLS the dialog's explicit stage (upscaling small videos like any
        // player), never sized by the element's own intrinsic size: intrinsic
        // sizing made layout jump when it flipped poster → real video, and a
        // native-size cap jumped against the viewport-filling poster beneath.
        "h-full w-full object-contain",
        className,
      )}
      // src on the ELEMENT, never via <source> children: per spec, mutating a
      // <source>'s src after insertion has NO effect — React reused this
      // element across swipes and the URL swap was silently ignored, so the
      // PREVIOUS video kept playing (audio behind the new item's poster, its
      // frames after the reveal). Setting src directly re-runs the media load
      // algorithm, aborting the old playback. It also drops the MIME-type
      // hint entirely, so browsers probe the actual bytes instead of
      // rejecting a container by its label (the old reason for a dual-source
      // mp4 fallback).
      src={url}
      onLoadedMetadata={() => onReady?.()}
      onCanPlay={() => onReady?.()}
      playsInline
      preload="metadata"
      poster={poster || undefined}
      // blob: source — unreachable for an AirPlay target (it fetches the URL
      // itself), and PiP dies when the dialog revokes the URL on close.
      disableRemotePlayback
      disablePictureInPicture
      {...({ "x-webkit-airplay": "deny" } as Record<string, string>)}
      // Only a genuine element failure sets `el.error`; spurious error events
      // without a MediaError (e.g. an aborted load during teardown) must not
      // raise the error card.
      onError={(e) => {
        const mediaError = e.currentTarget.error;
        if (mediaError) {
          onError?.(mediaError);
        }
      }}
      {...tapHandlers}
    />
  );
}
